import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  osnovaApi,
  useListDispatcherInboxQuery,
  useListDispatcherMailboxesQuery,
  useListDispatcherForwardsQuery,
  useGetDispatcherEmailQuery,
  useAnnotateDispatcherRoutingMutation,
  useMarkDispatcherEmailReadMutation,
} from '@shared/api';
import { MailboxForwardCard } from '@features/dispatcher-mailbox-routing';
import { MailboxRoutesTab } from './MailboxRoutesTab';
import { MailboxCategoriesTab } from './MailboxCategoriesTab';
import { SentTab } from './SentTab';
import { PolyusCodesTab } from './PolyusCodesTab';
import { PolyusLetterBlock } from './PolyusLetterBlock';
import { TONE_COLORS, ToneBadge, formatDt, type Tone } from './tone';
import { Button } from '@shared/ui/Button';
import { Skeleton } from '@shared/ui/Skeleton';
import { IconArrowLeft, IconDownload, IconInbox, IconTrash } from '@shared/ui/icons';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { ReadOnlyBanner, useAgentPermission } from '@entities/user';

// Узкий режим: страница уже 640px (маленькое окно, планшет, телефон).
// Список писем и письмо в две колонки там не помещаются — показываем их по
// очереди. Меряем ширину самой страницы, а не окна: открытое или свёрнутое
// боковое меню меняет место под письмо.
const NARROW = '@container dispatcher (max-width: 640px)';

// Главный shell страницы — табы сверху + контент. Высота вычитает
// AppShell-хедер (64px) и собственный TabBar (52px) чтобы overflow
// корректно работал внутри InboxCol/DetailCol.
const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  box-sizing: border-box;
  container-type: inline-size;
  container-name: dispatcher;
`;

const TabBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: 0 ${({ theme }) => theme.spacing(5)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};

  /* Вкладки переносятся на вторую строку, а не растягивают страницу вбок. */
  ${NARROW} {
    flex-wrap: wrap;
  }
`;

const TabBtn = styled.button<{ $active: boolean }>`
  position: relative;
  background: transparent;
  border: 0;
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.primary : theme.colors.text.secondary};
  cursor: pointer;
  transition: color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  ${NARROW} {
    white-space: nowrap;
  }

  &::after {
    content: '';
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: -1px;
    height: 2px;
    background: ${({ theme }) => theme.colors.primary};
    transform: scaleX(${({ $active }) => ($active ? 1 : 0)});
    transform-origin: center;
    transition: transform ${({ theme }) => theme.motion.base}
      ${({ theme }) => theme.motion.spring};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const RoutesScroll = styled.div`
  flex: 1 1 auto;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing(5)};
`;

const Wrap = styled.div`
  flex: 1 1 auto;
  display: grid;
  /* На небольших экранах список писем ужимается до 280px, чтобы самому письму
     оставалось место; minmax(0, 1fr) не даёт содержимому письма раздвинуть
     колонку шире экрана. */
  grid-template-columns: clamp(280px, 38%, 420px) minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(5)};
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;

  ${NARROW} {
    grid-template-columns: minmax(0, 1fr);
    padding: ${({ theme }) => theme.spacing(3)};
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// $letterOpen — открыто письмо: в узком режиме вместо списка видно письмо.
const InboxCol = styled.div<{ $letterOpen: boolean }>`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;

  ${NARROW} {
    display: ${({ $letterOpen }) => ($letterOpen ? 'none' : 'flex')};
  }
`;

const InboxHeader = styled.div`
  padding: ${({ theme }) => theme.spacing(4)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const InboxTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const InboxSubtitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
`;

const FilterChip = styled.button<{ $active: boolean }>`
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) =>
    $active ? `${theme.colors.primary}1a` : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.text.secondary};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ChipBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const SearchBar = styled.div`
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
  transition: border-color ${({ theme }) => theme.motion.fast};
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const RoutedBanner = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  background: linear-gradient(
    135deg,
    rgba(30, 142, 62, 0.18),
    ${({ theme }) => theme.colors.backgroundElevated}
  );
  border: 1px solid rgba(30, 142, 62, 0.45);
  border-radius: ${({ theme }) => theme.radius.md};
  text-decoration: none;
  color: inherit;
  font-size: 13px;
  transition:
    transform ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(30, 142, 62, 0.2);
  }
`;

const RoutedLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RoutedTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RoutedSub = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
`;

const SkippedNote = styled.div`
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const InboxList = styled.div`
  flex: 1;
  overflow-y: auto;
  scroll-behavior: smooth;
`;

const InboxItem = styled.button<{ $active: boolean; $unread: boolean; $index: number }>`
  width: 100%;
  text-align: left;
  border: 0;
  background: ${({ $active, theme }) =>
    $active ? `${theme.colors.primary}1a` : 'transparent'};
  border-left: 3px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  animation: ${fadeIn} 260ms ${({ theme }) => theme.motion.easeOut} both;
  animation-delay: ${({ $index }) => Math.min($index * 20, 400)}ms;
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const SenderLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  gap: 6px;
`;

const UnreadDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
  box-shadow: 0 0 6px ${({ theme }) => theme.colors.primary}88;
`;

const ClassFilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

/**
 * Спам-чип отдельной папки. Чтобы юрист сразу видел «спам открыт или нет»:
 *   - inactive: приглушённый красный текст, тонкая пунктирная рамка,
 *     прозрачный фон. Кнопка «спокойно ждёт», не давит на внимание.
 *   - active:   насыщенный красный, solid рамка, плотный фон. Сразу
 *     ясно «сейчас в режиме просмотра спама».
 * Иконка — IconTrash (lucide trash-2) вместо эмодзи 🛡: семантически
 * ближе и читается одинаково на всех платформах.
 */
const SpamChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  margin-left: auto;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px ${({ $active }) => ($active ? 'solid' : 'dashed')}
    ${({ $active }) => ($active ? '#d04848' : 'rgba(208, 72, 72, 0.45)')};
  background: ${({ $active }) =>
    $active ? 'rgba(208, 72, 72, 0.22)' : 'transparent'};
  color: ${({ $active }) => ($active ? '#d04848' : 'rgba(208, 72, 72, 0.55)')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  &:hover {
    background: rgba(208, 72, 72, 0.14);
    border-style: solid;
    border-color: #d04848;
    color: #d04848;
  }
`;

const AnnotationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const OutgoingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const OutgoingCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const OutgoingHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  flex-wrap: wrap;
`;

const OutgoingTo = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
`;

const OutgoingSubject = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const OutgoingDate = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  flex-shrink: 0;
`;

const OutgoingPreview = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.45;
`;

const AnnotationCard = styled.div<{ $tone: Tone }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ $tone }) => TONE_COLORS[$tone].border};
  border-left: 3px solid ${({ $tone }) => TONE_COLORS[$tone].fg};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const AnnoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  font-size: 12px;
`;

const AnnoActor = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AnnoMeta = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

const AnnoComment = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
  white-space: pre-wrap;
`;

const SenderText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const BellIcon = styled.span`
  display: inline-flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.muted};
  flex-shrink: 0;
  & svg {
    width: 14px;
    height: 14px;
  }
`;

function BellGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

const DateText = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const SubjectLine = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PreviewLine = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 6px;
`;

// Бейдж ящика-источника (мульти-ящик диспетчера): на какой из наших IMAP-ящиков
// пришло письмо. В отличие от статус-бейджей (ToneBadge, uppercase) — обычный
// регистр: внутри адрес почты, который в CAPS читается плохо. Иконка-конверт
// делает смысл считываемым без подписи.
const MailboxTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  min-width: 0;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border};

  & svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.text.muted};
  }
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DetailCol = styled.div<{ $letterOpen: boolean }>`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  /* Длинные адреса, ссылки и имена файлов без пробелов переносятся внутри
     карточки, а не растягивают её вбок. */
  overflow-wrap: anywhere;

  ${NARROW} {
    display: ${({ $letterOpen }) => ($letterOpen ? 'flex' : 'none')};
  }
`;

// Возврат к списку — только в узком режиме, где список и письмо идут по очереди.
const BackToList = styled.button`
  display: none;
  flex-shrink: 0;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  ${NARROW} {
    display: flex;
  }
`;

const DetailHeader = styled.div`
  padding: ${({ theme }) => theme.spacing(5)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const DetailTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const NotificationStrip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  background: rgba(245, 124, 0, 0.16);
  border: 1px solid rgba(245, 124, 0, 0.45);
  border-radius: ${({ theme }) => theme.radius.md};
  color: #f57c00;
  font-size: 12px;
  font-weight: 600;
  & svg {
    width: 16px;
    height: 16px;
  }
`;

const TitleBell = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: rgba(245, 124, 0, 0.18);
  color: #f57c00;
  border-radius: 50%;
  flex-shrink: 0;
  & svg {
    width: 16px;
    height: 16px;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const MetaItem = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  & strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 500;
  }
`;

const DetailBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(5)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  animation: ${fadeIn} 280ms ${({ theme }) => theme.motion.easeOut};
`;

const SectionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const ContentBox = styled.div`
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(4)};
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: ${({ theme }) => theme.colors.text.primary};
  max-height: 360px;
  overflow-y: auto;
`;

const SummaryCard = styled.div`
  position: relative;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary}14,
    ${({ theme }) => theme.colors.backgroundElevated}
  );
  border: 1px solid ${({ theme }) => theme.colors.primary}55;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
`;

const SummaryTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const FactCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FactLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.muted};
  font-weight: 600;
`;

const FactValue = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
`;

const NormsChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const NormChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SummarySkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

// Колонка minmax(0, 1fr), а не auto: колонка auto растягивалась под самое
// длинное имя файла, все строки вылезали за край письма вместе с кнопками
// «Скачать», а у карточки появлялась горизонтальная прокрутка.
const AttachmentList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(2)};
`;

// Имя файла переносится целиком; если места совсем мало, кнопка уходит на
// вторую строку справа, но всегда видна.
const AttachmentItem = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  transition: border-color ${({ theme }) => theme.motion.fast};
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FileIcon = styled.span`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary}22;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-weight: 600;
  font-size: 11px;
`;

const FileMeta = styled.div`
  flex: 1 1 160px;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FileName = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FileSize = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const AttachmentDownload = styled(Button)`
  margin-left: auto;
`;

const HitlBox = styled.div`
  position: relative;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const shimmerSweep = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const SavingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.colors.backgroundElevated}d8;
  backdrop-filter: blur(2px);
  border-radius: ${({ theme }) => theme.radius.md};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(2)};
  z-index: 2;
  overflow: hidden;
  animation: ${fadeIn} 180ms ${({ theme }) => theme.motion.easeOut};
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.colors.primary}33,
      transparent
    );
    animation: ${shimmerSweep} 1.5s linear infinite;
  }
`;

const SavingIcon = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid ${({ theme }) => theme.colors.border};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${keyframes`
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  `} 0.9s linear infinite;
`;

const SavingText = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  z-index: 1;
`;

const SavingHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  z-index: 1;
`;

const HitlButtonsRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const DecisionButton = styled.button<{ $tone: 'good' | 'neutral' | 'bad' }>`
  flex: 1;
  min-width: min(130px, 100%);
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  font-weight: 600;
  border: 1px solid
    ${({ $tone, theme }) =>
      $tone === 'good'
        ? theme.colors.status.success
        : $tone === 'bad'
          ? theme.colors.status.danger
          : theme.colors.border};
  background: transparent;
  color: ${({ $tone, theme }) =>
    $tone === 'good'
      ? theme.colors.status.success
      : $tone === 'bad'
        ? theme.colors.status.danger
        : theme.colors.text.secondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  &:hover:not(:disabled) {
    background: ${({ $tone, theme }) =>
      $tone === 'good'
        ? `${theme.colors.status.success}22`
        : $tone === 'bad'
          ? `${theme.colors.status.danger}22`
          : theme.colors.surfaceHover};
    transform: translateY(-1px);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RoutingSelect = styled.select`
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  outline: none;
  transition: border-color ${({ theme }) => theme.motion.fast};
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const CommentArea = styled.textarea`
  min-height: 70px;
  resize: vertical;
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font: inherit;
  font-size: 13px;
  line-height: 1.5;
  outline: none;
  transition: border-color ${({ theme }) => theme.motion.fast};
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 13px;
  text-align: center;
  padding: ${({ theme }) => theme.spacing(8)};
`;

const ToastSuccess = styled.div<{ $visible: boolean }>`
  position: fixed;
  bottom: ${({ theme }) => theme.spacing(5)};
  right: ${({ theme }) => theme.spacing(5)};
  background: ${({ theme }) => theme.colors.status.success};
  color: #fff;
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '12px')});
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition:
    opacity 240ms ease-out,
    transform 240ms ease-out;
`;

const ROUTING_OPTIONS = [
  { value: '', label: '— куда надо было направить? —' },
  { value: 'claim', label: 'Претензия / иск' },
  { value: 'contract', label: 'Договор' },
  { value: 'gov_request', label: 'Запрос госоргана' },
  { value: 'corporate', label: 'Корпоративные документы' },
  { value: 'bankruptcy', label: 'Банкротство' },
  { value: 'court_doc', label: 'Судебные документы' },
  { value: 'spam', label: 'Спам (реклама, маркетинг)' },
  { value: 'notification', label: 'Уведомление (системное, без действий)' },
  { value: 'returned', label: 'Не для юр.отдела — вернуть' },
  { value: 'other', label: 'Другое' },
];

const STATUS_LABELS: Record<string, string> = {
  received: 'Получено',
  classified: 'Классифицировано',
  routed: 'Перенаправлено',
  returned: 'Возвращено',
  failed: 'Ошибка обработки',
};

const STATUS_TONE: Record<string, Tone> = {
  received: 'info',
  classified: 'warning',
  routed: 'success',
  returned: 'danger',
  failed: 'danger',
};

/** letter_type из agent_classification → русский лейбл. */
const LETTER_TYPE_LABELS: Record<string, string> = {
  pretension: 'Претензия',
  gov_request: 'Запрос госоргана',
  court_doc: 'Судебный документ',
  contract_draft: 'Проект договора',
  bankruptcy: 'Банкротство',
  corporate: 'Корпоративный',
  other: 'Прочее',
  spam: 'Спам',
  // Из dispatcher rule-based классификатора:
  auto_notification: 'Автоуведомление',
  forward_to_statut: 'Юр.отдел',
};

const LETTER_TYPE_TONE: Record<string, Tone> = {
  pretension: 'danger',
  gov_request: 'accent',
  court_doc: 'purple',
  contract_draft: 'info',
  bankruptcy: 'warning',
  corporate: 'info',
  other: 'neutral',
  // Спам визуально выделен (danger-tone) чтобы быстро отличить ошибочно
  // попавшие в общий Inbox письма — на фоне обычных нейтральных классов
  // он сразу бросается в глаза.
  spam: 'danger',
  notification: 'neutral',
  auto_notification: 'neutral',
  forward_to_statut: 'success',
};

function letterTypeLabel(code?: string | null): string {
  if (!code) return '—';
  return LETTER_TYPE_LABELS[code] ?? code;
}
function letterTypeTone(code?: string | null): Tone {
  if (!code) return 'neutral';
  return LETTER_TYPE_TONE[code] ?? 'neutral';
}

/** Метка ящика-источника для UI. Пустое/NULL `source_mailbox` — legacy-письмо
 *  (до мульти-ящика) или единственный ящик — показываем как «Основной». */
function mailboxLabel(m?: string | null): string {
  return m && m.trim() ? m : 'Основной';
}

type StatusFilter = '' | 'received' | 'classified' | 'routed' | 'returned';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

function fileExt(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot < 0) return '???';
  return filename.slice(dot + 1, dot + 5).toUpperCase();
}

/**
 * Эвристика «это просто уведомление, без юр.содержания, юр.действий не требует».
 *
 * Используется для двух UI-эффектов:
 *   1. Иконка-колокольчик слева от отправителя в InboxItem.
 *   2. Бейдж «Уведомление» рядом с заголовком в DetailHeader.
 *
 * Сигналы (любой из них достаточен):
 *   - letter_type явно указывает: auto_notification / spam / notification.
 *   - Sender — технический ящик (noreply, guard@arbitr, mailer-daemon, ...).
 *   - Sender — домен системы автоматических уведомлений (arbitr.ru, sudrf.ru,
 *     gov.ru, mos.ru и др. государственные/судебные системы).
 *   - Subject содержит характерные слова уведомления.
 *   - Body содержит «Система … уведомляет», «Вы были указаны в качестве
 *     участника», «электронном виде» — типичные шаблоны электронного
 *     правосудия и подобных систем.
 */
function isJustNotification(
  letterType: string | null | undefined,
  sender: string | null | undefined,
  subject: string | null | undefined,
  body?: string | null,
): boolean {
  if (
    letterType === 'auto_notification' ||
    letterType === 'spam' ||
    letterType === 'notification'
  ) {
    return true;
  }
  const s = (sender ?? '').toLowerCase();

  // Технические ящики по локальной части (часть до @).
  if (
    /(^|[._-])(noreply|no-reply|donotreply|do-not-reply|mailer-daemon|notification|notify|robot|news|info|guard|system|auto|service|mail|support|admin)@/.test(
      s,
    )
  ) {
    return true;
  }

  // Домены систем, которые шлют только машинные уведомления:
  //   *.arbitr.ru     — «Электронное правосудие» (kad.arbitr.ru, my.arbitr.ru),
  //   *.sudrf.ru      — портал ГАС «Правосудие»,
  //   *.gosuslugi.ru  — госуслуги (рассылки),
  //   nalog.gov.ru    — налоговая (квитанции/уведомления),
  //   *.fssp.gov.ru   — приставы (нотификации о возбуждении и т.п.).
  // Подразумевается: если по делу нужны действия — юрист получит документ
  // через ЭДО или личный кабинет, а почтовое письмо — лишь уведомительное.
  if (
    /@([a-z0-9-]+\.)?(arbitr|sudrf)\.ru\b/.test(s) ||
    /@([a-z0-9-]+\.)?gosuslugi\.ru\b/.test(s) ||
    /@nalog\.gov\.ru\b/.test(s) ||
    /@([a-z0-9-]+\.)?fssp\.gov\.ru\b/.test(s)
  ) {
    return true;
  }

  const subj = (subject ?? '').toLowerCase();
  if (
    subj.startsWith('уведомление') ||
    subj.startsWith('notification:') ||
    subj.startsWith('[noreply]') ||
    subj.startsWith('принято(а)') ||
    subj.startsWith('принято ') ||
    subj.startsWith('зарегистрировано') ||
    subj.includes('автоматическое уведомление') ||
    subj.includes('automatic notification') ||
    subj.includes('электронное правосудие') ||
    subj.includes('электронном виде')
  ) {
    return true;
  }

  const text = (body ?? '').toLowerCase();
  if (text) {
    if (
      text.includes("система 'электронное правосудие' уведомляет") ||
      text.includes('система «электронное правосудие» уведомляет') ||
      text.includes('система "электронное правосудие" уведомляет') ||
      text.includes('вы были указаны в качестве участника') ||
      text.includes('поступили в систему подачи документов') ||
      text.includes('зарегистрировано арбитражным судом') ||
      text.includes('настоящее письмо сформировано автоматически') ||
      text.includes('настоящее сообщение сформировано автоматически') ||
      text.includes('please do not reply to this email')
    ) {
      return true;
    }
  }
  return false;
}

export function DispatcherPage() {
  // Главная навигация страницы: «Входящие» (inbox + карточка письма),
  // «Ждут решения» (письма, которым ОСНОВА не нашла адресата),
  // «Отправленные» (письма, перенаправленные на ящики отделов),
  // «Маршруты отделов» (справочник mailbox_routes для агента-диспетчера),
  // «Категории» и «Шифры ПОЛЮС» (журнал шифров корреспонденции Заказчика).
  const [activeView, setActiveView] = useState<
    'inbox' | 'review' | 'sent' | 'routes' | 'categories' | 'polyus'
  >('inbox');
  const { can_read, can_write } = useAgentPermission('dispatcher');
  // Сколько писем ждут решения — число на вкладке (limit 1: нужен только total).
  const { data: reviewCount } = useListDispatcherForwardsQuery({
    status: 'review',
    limit: 1,
    offset: 0,
  });
  const reviewTotal = reviewCount?.total ?? 0;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [letterTypeFilter, setLetterTypeFilter] = useState<string>('');
  // Фильтр по ящику-источнику. '' = все ящики (фильтр не применяется).
  // Строка чипов рендерится только когда ящиков > 1 (см. mailboxes ниже).
  const [mailboxFilter, setMailboxFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [markRead] = useMarkDispatcherEmailReadMutation();
  const [decision, setDecision] = useState<'accepted' | 'disputed' | 'rejected' | null>(null);
  const [correctRouting, setCorrectRouting] = useState('');
  const [comment, setComment] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  // Debounce поиска — 280мс. Не дёргаем backend на каждый символ,
  // ждём паузы в наборе. Минимум 2 символа: меньше — фильтр сбрасывается
  // (бессмысленно искать «А» по 1000 писем).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed.length > 0 && trimmed.length < 2) {
      setSearchQuery('');
      return;
    }
    const t = window.setTimeout(() => setSearchQuery(trimmed), 280);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const dispatch = useAppDispatch();

  /**
   * Префетч карточки письма при наведении мыши. К моменту клика данные
   * уже в кэше RTK Query — клик переключает selectedId, useGetDispatcherEmailQuery
   * мгновенно возвращает кэшированный результат, ничего не «тупит».
   * 200мс debounce защищает от случайных проносов мышью.
   */
  const prefetchTimers = useMemo(() => new Map<string, number>(), []);
  function prefetchEmail(id: string) {
    if (prefetchTimers.has(id)) return;
    const t = window.setTimeout(() => {
      dispatch(
        osnovaApi.util.prefetch('getDispatcherEmail' as never, id as never, {
          ifOlderThan: 60,
        }),
      );
      prefetchTimers.delete(id);
    }, 180);
    prefetchTimers.set(id, t);
  }
  function cancelPrefetch(id: string) {
    const t = prefetchTimers.get(id);
    if (t) {
      window.clearTimeout(t);
      prefetchTimers.delete(id);
    }
  }

  // Агрегация входящих по ящику-источнику — для переключателя ящиков.
  const { data: mailboxes } = useListDispatcherMailboxesQuery();
  // Реальные ящики (без legacy-бакета '' = письма до мульти-ящика). Переключатель
  // показываем только при ≥2 реальных ящиках; legacy-письма видны под «Все ящики».
  const realMailboxes = (mailboxes ?? []).filter((m) => m.mailbox !== '');
  // Метку ящика-источника внутри письма показываем ровно тогда же, когда и
  // чипы-переключатель сверху — при ≥2 реальных ящиках. На одиночной установке
  // (или пока все письма legacy) это лишний шум: источник всегда «Основной».
  const showMailbox = realMailboxes.length > 1;

  const { data: inboxData, isLoading: inboxLoading } = useListDispatcherInboxQuery({
    status: statusFilter || undefined,
    search: searchQuery || undefined,
    letter_type: letterTypeFilter || undefined,
    // Пустая строка = «все ящики»: параметр не отправляется, backend не фильтрует.
    mailbox: mailboxFilter || undefined,
    // Inbox показывает только входящие. Исходящие появляются как связанный
    // блок «Ответы из нашей почты» в карточке входящего письма, не как
    // отдельная вкладка списка.
    direction: 'incoming',
    limit: 100,
    offset: 0,
  });

  const items = inboxData?.items ?? [];
  const total = inboxData?.total ?? 0;

  const { data: detail, isLoading: detailLoading } = useGetDispatcherEmailQuery(
    selectedId ?? ('' as string),
    { skip: !selectedId },
  );

  const [annotate, { isLoading: isAnnotating }] = useAnnotateDispatcherRoutingMutation();

  const selectedCorr = detail?.correspondence;
  const attachments = detail?.attachments ?? [];

  /** Полный разбор LLM-классификации письма для блока «О чём». */
  interface AgentClassification {
    letter_type?: string | null;
    counterparty?: string | null;
    dispute_type?: string | null;
    applicable_norms?: string[] | null;
    response_deadline_days?: number | null;
    response_deadline_basis?: string | null;
    summary?: string | null;
    return_reason?: string | null;
    /** Договор из письма не заведён: во вложениях нет текста договора. */
    contract_autocreate?: { status?: string; reason?: string } | null;
  }

  const agentMeta = useMemo<AgentClassification | null>(() => {
    return (selectedCorr?.agent_classification ?? null) as AgentClassification | null;
  }, [selectedCorr]);

  const agentLetterType = agentMeta?.letter_type ?? null;
  const detailIsNotification = useMemo(
    () =>
      isJustNotification(
        agentLetterType,
        selectedCorr?.sender,
        selectedCorr?.subject,
        selectedCorr?.content_text,
      ),
    [agentLetterType, selectedCorr?.sender, selectedCorr?.subject, selectedCorr?.content_text],
  );

  async function submitDecision() {
    if (!selectedId || !decision) return;
    try {
      const result = await annotate({
        id: selectedId,
        decision,
        correct_routing: correctRouting || undefined,
        comment: comment.trim() || undefined,
      }).unwrap();

      // Сообщение от бэкенда приоритетнее — там написано что реально
      // произошло (создан claim/contract или просто запомнили).
      const msg =
        result.message ??
        (decision === 'accepted'
          ? 'Запомнил как верное решение'
          : 'Запомнил исправление — учту в будущих письмах');
      setToast(msg);

      setDecision(null);
      setCorrectRouting('');
      setComment('');
      // Если реклассификация создала claim/contract — держим toast подольше,
      // чтобы юрист успел прочитать «Создана претензия в реестре».
      const ttl = result.routed ? 4500 : 2400;
      window.setTimeout(() => setToast(null), ttl);
    } catch {
      setToast('Не удалось сохранить — попробуйте ещё раз');
      window.setTimeout(() => setToast(null), 2400);
    }
  }

  function buildAttachmentUrl(corrId: string, attId: string): string {
    const base =
      (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';
    return `${base}/agents/statut/dispatcher/inbox/${corrId}/attachments/${attId}`;
  }

  async function downloadAttachment(corrId: string, attId: string, filename: string) {
    const url = buildAttachmentUrl(corrId, attId);
    try {
      const resp = await fetch(url, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      if (!resp.ok) {
        setToast(`Ошибка ${resp.status} при скачивании`);
        window.setTimeout(() => setToast(null), 2400);
        return;
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setToast('Не удалось скачать файл');
      window.setTimeout(() => setToast(null), 2400);
    }
  }

  return (
    <PageShell>
      <TabBar>
        <TabBtn
          $active={activeView === 'inbox'}
          onClick={() => setActiveView('inbox')}
          type="button"
        >
          Входящие
        </TabBtn>
        <TabBtn
          $active={activeView === 'review'}
          onClick={() => setActiveView('review')}
          type="button"
        >
          Ждут решения{reviewTotal > 0 ? ` · ${reviewTotal}` : ''}
        </TabBtn>
        <TabBtn
          $active={activeView === 'sent'}
          onClick={() => setActiveView('sent')}
          type="button"
        >
          Отправленные
        </TabBtn>
        <TabBtn
          $active={activeView === 'routes'}
          onClick={() => setActiveView('routes')}
          type="button"
        >
          Маршруты отделов
        </TabBtn>
        <TabBtn
          $active={activeView === 'categories'}
          onClick={() => setActiveView('categories')}
          type="button"
        >
          Категории
        </TabBtn>
        <TabBtn
          $active={activeView === 'polyus'}
          onClick={() => setActiveView('polyus')}
          type="button"
        >
          Шифры ПОЛЮС
        </TabBtn>
      </TabBar>

      {can_read && !can_write && (
        <div style={{ padding: '12px 20px 0' }}>
          <ReadOnlyBanner />
        </div>
      )}

      {activeView === 'routes' ? (
        <RoutesScroll>
          <MailboxRoutesTab />
        </RoutesScroll>
      ) : activeView === 'categories' ? (
        <RoutesScroll>
          <MailboxCategoriesTab />
        </RoutesScroll>
      ) : activeView === 'polyus' ? (
        <RoutesScroll>
          <PolyusCodesTab
            onOpenLetter={(correspondenceId) => {
              // Письмо Заказчику, отправленное из ОСНОВЫ или найденное в
              // «Отправленных», — в знакомой карточке (detail грузится по id).
              setSelectedId(correspondenceId);
              setActiveView('inbox');
            }}
          />
        </RoutesScroll>
      ) : activeView === 'sent' || activeView === 'review' ? (
        <RoutesScroll>
          <SentTab
            mode={activeView}
            onOpen={(correspondenceId) => {
              // Открываем знакомую карточку письма во вкладке «Входящие»:
              // detail подгружается по id независимо от inbox-списка, так что
              // карточка с MailboxForwardCard откроется корректно.
              setSelectedId(correspondenceId);
              setActiveView('inbox');
            }}
          />
        </RoutesScroll>
      ) : (
    <Wrap>
      <InboxCol $letterOpen={!!selectedId}>
        <InboxHeader>
          <div>
            <InboxTitle>Входящие диспетчера</InboxTitle>
            <InboxSubtitle>
              Всего писем: {total}
              {inboxLoading ? ' · загрузка…' : ''}
            </InboxSubtitle>
          </div>
        </InboxHeader>
        {/* Переключатель ящиков-источников. Рендерим только когда реальных
            ящиков ≥2 — при единственном ящике строка бессмысленна. Legacy-письма
            (source_mailbox IS NULL) отдельным чипом не показываем: они видны
            под «Все ящики». */}
        {realMailboxes.length > 1 && (
          <ChipBar>
            <FilterChip
              $active={!mailboxFilter}
              onClick={() => setMailboxFilter('')}
              title="Показать письма всех ящиков"
            >
              Все ящики
            </FilterChip>
            {realMailboxes.map((m) => {
              const active = mailboxFilter === m.mailbox;
              return (
                <FilterChip
                  key={m.mailbox}
                  $active={active}
                  // Повторный клик по активному ящику снимает фильтр.
                  onClick={() =>
                    setMailboxFilter(mailboxFilter === m.mailbox ? '' : m.mailbox)
                  }
                  title={`${m.mailbox}: всего ${m.total}${
                    m.unread > 0 ? `, непрочитанных ${m.unread}` : ''
                  }`}
                >
                  {m.mailbox}
                  {m.unread > 0 ? ` · ${m.unread}` : ''}
                </FilterChip>
              );
            })}
          </ChipBar>
        )}
        <SearchBar>
          <SearchInput
            type="search"
            placeholder="Поиск: отправитель, тема или текст письма…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </SearchBar>
        <ChipBar>
          {(
            [
              ['', 'Все'],
              ['received', 'Получено'],
              ['classified', 'Классифицировано'],
              ['routed', 'Перенаправлено'],
              ['returned', 'Возвращено'],
            ] as Array<[StatusFilter, string]>
          ).map(([val, label]) => (
            <FilterChip
              key={val || 'all'}
              $active={statusFilter === val}
              onClick={() => setStatusFilter(val)}
            >
              {label}
            </FilterChip>
          ))}
        </ChipBar>
        <ClassFilterBar>
          {(
            [
              ['pretension', 'Претензии'],
              ['court_doc', 'Суд.документы'],
              ['contract_draft', 'Договоры'],
              ['gov_request', 'Госорганы'],
              ['corporate', 'Корпоративные'],
              ['bankruptcy', 'Банкротство'],
              ['notification', 'Уведомления'],
            ] as Array<[string, string]>
          ).map(([val, label]) => (
            <FilterChip
              key={val}
              $active={letterTypeFilter === val}
              // Клик по активному чипу снимает фильтр — возвращает к
              // полному списку (без явного чипа «Все»).
              onClick={() => setLetterTypeFilter(letterTypeFilter === val ? '' : val)}
            >
              {label}
            </FilterChip>
          ))}
          {/* Спам — отдельная папка справа, визуально выделена красным
              чтобы её не путали с обычными категориями. По клику снимается. */}
          <SpamChip
            $active={letterTypeFilter === 'spam'}
            onClick={() => setLetterTypeFilter(letterTypeFilter === 'spam' ? '' : 'spam')}
            title="Папка спама — реклама, маркетинговые рассылки и подобное"
          >
            <IconTrash size={14} />
            Спам
          </SpamChip>
        </ClassFilterBar>
        <InboxList>
          {inboxLoading && items.length === 0 ? (
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} $height={62} $radius="8px" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState>Писем не найдено</EmptyState>
          ) : (
            items.map((it, idx) => {
              const meta = (it.agent_classification ?? null) as
                | { letter_type?: string }
                | null;
              const notification = isJustNotification(
                meta?.letter_type,
                it.sender,
                it.subject,
                it.content_text,
              );
              return (
                <InboxItem
                  key={it.id}
                  $active={selectedId === it.id}
                  $unread={it.status === 'received'}
                  $index={idx}
                  onClick={() => {
                    setSelectedId(it.id);
                    if (it.is_unread) {
                      // Fire-and-forget: backend идемпотентно ставит read,
                      // RTK Query инвалидирует список → точка пропадает.
                      void markRead(it.id);
                    }
                  }}
                  onMouseEnter={() => prefetchEmail(it.id)}
                  onMouseLeave={() => cancelPrefetch(it.id)}
                  onFocus={() => prefetchEmail(it.id)}
                >
                  <SenderLine>
                    <SenderText>
                      {it.is_unread && (
                        <UnreadDot title="Непрочитанное письмо" />
                      )}
                      {notification && (
                        <BellIcon title="Просто уведомление — юр.действий не требует">
                          <BellGlyph />
                        </BellIcon>
                      )}
                      {it.sender ?? '(без отправителя)'}
                    </SenderText>
                    <DateText>{formatDt(it.incoming_at)}</DateText>
                  </SenderLine>
                  <SubjectLine>{it.subject ?? '(без темы)'}</SubjectLine>
                  <PreviewLine>
                    {(it.content_text ?? '').slice(0, 140) || '(пустое сообщение)'}
                  </PreviewLine>
                  <StatusRow>
                    {showMailbox && (
                      <MailboxTag
                        title={`Письмо пришло на ящик: ${mailboxLabel(it.source_mailbox)}`}
                      >
                        <IconInbox size={12} />
                        <span>{mailboxLabel(it.source_mailbox)}</span>
                      </MailboxTag>
                    )}
                    <ToneBadge $tone={STATUS_TONE[it.status] ?? 'neutral'}>
                      {STATUS_LABELS[it.status] ?? it.status}
                    </ToneBadge>
                    {meta?.letter_type && (
                      <ToneBadge $tone={letterTypeTone(meta.letter_type)}>
                        {letterTypeLabel(meta.letter_type)}
                      </ToneBadge>
                    )}
                    {notification && (
                      <ToneBadge $tone="neutral">Уведомление</ToneBadge>
                    )}
                    {it.returned_to_dispatcher && (
                      <ToneBadge $tone="danger">Вернули</ToneBadge>
                    )}
                    {it.polyus_code && (
                      <ToneBadge $tone="info" title="Шифр проектной корреспонденции ПОЛЮС">
                        {it.polyus_code}
                      </ToneBadge>
                    )}
                    {it.polyus_reg_no && (
                      <ToneBadge $tone="info" title="Регистрационный номер письма ООО «Полюс Сухой Лог»">
                        {it.polyus_reg_no}
                      </ToneBadge>
                    )}
                  </StatusRow>
                </InboxItem>
              );
            })
          )}
        </InboxList>
      </InboxCol>

      <DetailCol $letterOpen={!!selectedId}>
        <BackToList type="button" onClick={() => setSelectedId(null)}>
          <IconArrowLeft size={14} />
          К списку писем
        </BackToList>
        {!selectedId ? (
          <EmptyState>
            Выберите письмо из списка слева, чтобы посмотреть содержание,
            <br />
            проверить классификацию диспетчера и обучить агента.
          </EmptyState>
        ) : detailLoading || !selectedCorr ? (
          <div style={{ padding: 24 }}>
            <Skeleton $height={32} $width="60%" />
            <div style={{ height: 12 }} />
            <Skeleton $height={16} $width="40%" />
            <div style={{ height: 24 }} />
            <Skeleton $height={120} />
          </div>
        ) : (
          <>
            <DetailHeader>
              {detailIsNotification && (
                <NotificationStrip>
                  <BellGlyph />
                  Уведомительное письмо — действий, скорее всего, не требует.
                  Документ по делу придёт через ЭДО / личный кабинет.
                </NotificationStrip>
              )}
              <DetailTitle>
                {detailIsNotification && (
                  <TitleBell title="Уведомительное письмо">
                    <BellGlyph />
                  </TitleBell>
                )}
                <span>{selectedCorr.subject ?? '(без темы)'}</span>
              </DetailTitle>
              <Meta>
                <MetaItem>
                  От: <strong>{selectedCorr.sender ?? '(не указан)'}</strong>
                </MetaItem>
                <MetaItem>
                  Получено: <strong>{formatDt(selectedCorr.incoming_at)}</strong>
                </MetaItem>
                {showMailbox && (
                  <MetaItem title="На какой из наших ящиков пришло письмо">
                    <IconInbox size={14} />
                    На ящик:{' '}
                    <strong>{mailboxLabel(selectedCorr.source_mailbox)}</strong>
                  </MetaItem>
                )}
                <MetaItem>
                  Статус:{' '}
                  <ToneBadge $tone={STATUS_TONE[selectedCorr.status] ?? 'neutral'}>
                    {STATUS_LABELS[selectedCorr.status] ?? selectedCorr.status}
                  </ToneBadge>
                </MetaItem>
                {agentLetterType && (
                  <MetaItem>
                    Класс агента:{' '}
                    <ToneBadge $tone={letterTypeTone(agentLetterType)}>
                      {letterTypeLabel(agentLetterType)}
                    </ToneBadge>
                  </MetaItem>
                )}
                {(selectedCorr.cc ?? []).filter((a) => a.trim()).length > 0 && (
                  <MetaItem>
                    Копия (cc):{' '}
                    <strong>
                      {(selectedCorr.cc ?? [])
                        .filter((a) => a.trim())
                        .join(', ')}
                    </strong>
                  </MetaItem>
                )}
                {selectedCorr.returned_to_dispatcher && (
                  <MetaItem>
                    <ToneBadge $tone="danger">Возвращено диспетчеру</ToneBadge>
                  </MetaItem>
                )}
              </Meta>
            </DetailHeader>
            <DetailBody>
              {/* === БАННЕР: куда перенаправлено агентом-диспетчером === */}
              {detail?.routed_to && (
                <RoutedBanner to={detail.routed_to.url}>
                  <RoutedLeft>
                    <RoutedTitle>
                      ✓ Перенаправлено в реестр{' '}
                      {detail.routed_to.kind === 'claim' ? 'Претензий/исков' : 'Договоров'}
                    </RoutedTitle>
                    <RoutedSub>{detail.routed_to.label} — открыть карточку →</RoutedSub>
                  </RoutedLeft>
                  <ToneBadge $tone="success">Готово</ToneBadge>
                </RoutedBanner>
              )}
              {!detail?.routed_to && agentMeta?.contract_autocreate?.status === 'skipped' && (
                <SkippedNote>
                  Договор в реестр не заведён: {agentMeta.contract_autocreate.reason}. Если во
                  вложении всё-таки договор — укажите «Договор» в блоке «Обучение агента» ниже,
                  ОСНОВА заведёт его.
                </SkippedNote>
              )}

              {/* === КРАТКАЯ ВЫЖИМКА (от LLM-классификатора) === */}
              {agentMeta && (agentMeta.summary || agentMeta.dispute_type || agentMeta.counterparty) ? (
                <SummaryCard>
                  <SummaryHeader>
                    <span>О чём это письмо</span>
                    <ToneBadge $tone={letterTypeTone(agentLetterType)}>
                      {letterTypeLabel(agentLetterType)}
                    </ToneBadge>
                  </SummaryHeader>
                  {agentMeta.summary && (
                    <SummaryTitle>{agentMeta.summary}</SummaryTitle>
                  )}
                  <SummaryGrid>
                    {agentMeta.counterparty && (
                      <FactCell>
                        <FactLabel>Контрагент</FactLabel>
                        <FactValue>{agentMeta.counterparty}</FactValue>
                      </FactCell>
                    )}
                    {agentMeta.dispute_type && (
                      <FactCell>
                        <FactLabel>Тип спора</FactLabel>
                        <FactValue>{agentMeta.dispute_type}</FactValue>
                      </FactCell>
                    )}
                    {agentMeta.response_deadline_days != null && (
                      <FactCell>
                        <FactLabel>Срок ответа</FactLabel>
                        <FactValue>
                          {agentMeta.response_deadline_days} дн.
                          {agentMeta.response_deadline_basis
                            ? ` · ${agentMeta.response_deadline_basis}`
                            : ''}
                        </FactValue>
                      </FactCell>
                    )}
                    {agentMeta.return_reason && (
                      <FactCell>
                        <FactLabel>Почему возвращено</FactLabel>
                        <FactValue>{agentMeta.return_reason}</FactValue>
                      </FactCell>
                    )}
                  </SummaryGrid>
                  {agentMeta.applicable_norms && agentMeta.applicable_norms.length > 0 && (
                    <FactCell>
                      <FactLabel>Применимые нормы</FactLabel>
                      <NormsChips>
                        {agentMeta.applicable_norms.map((n) => (
                          <NormChip key={n}>{n}</NormChip>
                        ))}
                      </NormsChips>
                    </FactCell>
                  )}
                </SummaryCard>
              ) : (
                <SummarySkeleton>
                  Письмо ещё анализируется агентом — выжимка появится здесь
                  через 30–60 секунд после получения.
                </SummarySkeleton>
              )}

              {detail?.polyus && (
                <div>
                  <SectionTitle>Шифр ПОЛЮС</SectionTitle>
                  <PolyusLetterBlock info={detail.polyus} onOpenLetter={setSelectedId} />
                </div>
              )}

              <div>
                <SectionTitle>Содержание</SectionTitle>
                <ContentBox>
                  {selectedCorr.content_text?.trim() || '(пустое сообщение)'}
                </ContentBox>
              </div>

              {attachments.length > 0 && (
                <div>
                  <SectionTitle>Вложения ({attachments.length})</SectionTitle>
                  <AttachmentList>
                    {attachments.map((a) => (
                      <AttachmentItem key={a.id}>
                        <FileIcon>{fileExt(a.filename)}</FileIcon>
                        <FileMeta>
                          <FileName>{a.filename}</FileName>
                          <FileSize>{formatSize(a.size_bytes)}</FileSize>
                        </FileMeta>
                        <AttachmentDownload
                          $variant="ghost"
                          $size="sm"
                          aria-label={`Скачать ${a.filename}`}
                          onClick={() =>
                            downloadAttachment(selectedCorr.id, a.id, a.filename)
                          }
                        >
                          <IconDownload size={14} />
                          Скачать
                        </AttachmentDownload>
                      </AttachmentItem>
                    ))}
                  </AttachmentList>
                </div>
              )}

              <div>
                <SectionTitle>Маршрут на ящик отдела</SectionTitle>
                <MailboxForwardCard correspondenceId={detail.correspondence.id} />
              </div>

              <div>
                <SectionTitle>Обучение агента</SectionTitle>
                <HitlBox>
                  {isAnnotating && (
                    <SavingOverlay>
                      <SavingIcon />
                      <SavingText>Запоминаю решение…</SavingText>
                      <SavingHint>
                        Запись в память агента. Анализ контекста уйдёт фоном.
                      </SavingHint>
                    </SavingOverlay>
                  )}
                  <div style={{ fontSize: 13, color: 'inherit' }}>
                    Агент классифицировал как{' '}
                    <ToneBadge $tone={letterTypeTone(agentLetterType)}>
                      {letterTypeLabel(agentLetterType)}
                    </ToneBadge>{' '}
                    — подскажите, верно ли это?
                  </div>
                  <HitlButtonsRow>
                    <DecisionButton
                      $tone="good"
                      onClick={() => setDecision('accepted')}
                      disabled={isAnnotating || !can_write}
                      title={!can_write ? 'Только чтение' : undefined}
                      style={{
                        background: decision === 'accepted' ? '#28a74522' : undefined,
                      }}
                    >
                      ✓ Верно обработано
                    </DecisionButton>
                    <DecisionButton
                      $tone="neutral"
                      onClick={() => setDecision('disputed')}
                      disabled={isAnnotating || !can_write}
                      title={!can_write ? 'Только чтение' : undefined}
                      style={{
                        background: decision === 'disputed' ? '#88888822' : undefined,
                      }}
                    >
                      ± Частично верно
                    </DecisionButton>
                    <DecisionButton
                      $tone="bad"
                      onClick={() => setDecision('rejected')}
                      disabled={isAnnotating || !can_write}
                      title={!can_write ? 'Только чтение' : undefined}
                      style={{
                        background: decision === 'rejected' ? '#d0484822' : undefined,
                      }}
                    >
                      ✗ Ошибка классификации
                    </DecisionButton>
                  </HitlButtonsRow>

                  {(decision === 'disputed' || decision === 'rejected') && (
                    <RoutingSelect
                      value={correctRouting}
                      onChange={(e) => setCorrectRouting(e.target.value)}
                    >
                      {ROUTING_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </RoutingSelect>
                  )}

                  {decision && (
                    <CommentArea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Поясните, почему такое решение — это поможет агенту лучше распознавать похожие письма."
                    />
                  )}

                  {decision && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      <Button
                        $variant="ghost"
                        $size="sm"
                        onClick={() => {
                          setDecision(null);
                          setCorrectRouting('');
                          setComment('');
                        }}
                        disabled={isAnnotating}
                      >
                        Отмена
                      </Button>
                      <Button
                        onClick={submitDecision}
                        disabled={isAnnotating || !can_write}
                        title={!can_write ? 'Только чтение' : undefined}
                      >
                        {isAnnotating ? 'Запоминаю…' : 'Запомнить'}
                      </Button>
                    </div>
                  )}
                </HitlBox>
              </div>

              {detail?.annotations && detail.annotations.length > 0 && (
                <div>
                  <SectionTitle>
                    Оценки коллег ({detail.annotations.length})
                  </SectionTitle>
                  <AnnotationsList>
                    {detail.annotations.map((a) => {
                      const tone: Tone =
                        a.decision === 'accepted'
                          ? 'success'
                          : a.decision === 'rejected'
                            ? 'danger'
                            : a.decision === 'disputed'
                              ? 'warning'
                              : 'neutral';
                      const decisionLabel =
                        a.decision === 'accepted'
                          ? 'Верно обработано'
                          : a.decision === 'disputed'
                            ? 'Частично верно'
                            : a.decision === 'rejected'
                              ? 'Ошибка классификации'
                              : (a.decision ?? '—');
                      return (
                        <AnnotationCard key={a.id} $tone={tone}>
                          <AnnoHeader>
                            <AnnoActor>
                              {a.actor_full_name ?? a.actor_email ?? 'Юрист'}
                            </AnnoActor>
                            <ToneBadge $tone={tone}>{decisionLabel}</ToneBadge>
                            {a.risk_category && (
                              <ToneBadge $tone="info">
                                {letterTypeLabel(a.risk_category)}
                              </ToneBadge>
                            )}
                            {!a.is_active && (
                              <ToneBadge $tone="neutral">отозвана</ToneBadge>
                            )}
                            <AnnoMeta>{formatDt(a.created_at)}</AnnoMeta>
                          </AnnoHeader>
                          {a.comment && <AnnoComment>{a.comment}</AnnoComment>}
                        </AnnotationCard>
                      );
                    })}
                  </AnnotationsList>
                </div>
              )}

              {detail?.related_outgoing && detail.related_outgoing.length > 0 && (
                <div>
                  <SectionTitle>
                    Куда было отправлено / ответы из нашей почты (
                    {detail.related_outgoing.length})
                  </SectionTitle>
                  <OutgoingList>
                    {detail.related_outgoing.map((o) => (
                      <OutgoingCard key={o.id}>
                        <OutgoingHeader>
                          <OutgoingSubject>
                            {o.subject ?? '(без темы)'}
                          </OutgoingSubject>
                          <OutgoingDate>{formatDt(o.incoming_at)}</OutgoingDate>
                        </OutgoingHeader>
                        <OutgoingTo>
                          → {(o.recipients ?? []).join(', ') || '(без адресата)'}
                          {o.cc && o.cc.length > 0 && (
                            <> · копия: {o.cc.join(', ')}</>
                          )}
                        </OutgoingTo>
                        {o.content_text && (
                          <OutgoingPreview>
                            {o.content_text.slice(0, 240)}
                          </OutgoingPreview>
                        )}
                      </OutgoingCard>
                    ))}
                  </OutgoingList>
                </div>
              )}

              {detail?.routing_errors && detail.routing_errors.length > 0 && (
                <div>
                  <SectionTitle>История исправлений</SectionTitle>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {detail.routing_errors.map((e) => (
                      <div
                        key={e.id}
                        style={{
                          fontSize: 12,
                          padding: 10,
                          background: 'rgba(0,0,0,0.04)',
                          borderRadius: 8,
                        }}
                      >
                        <div style={{ marginBottom: 4 }}>
                          <strong>{formatDt(e.created_at)}</strong> ·{' '}
                          {e.dispatcher_department ?? '—'} →{' '}
                          {e.expected_department ?? '—'}
                        </div>
                        {e.reason && <div>{e.reason}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </DetailBody>
          </>
        )}
      </DetailCol>

      <ToastSuccess $visible={!!toast}>{toast}</ToastSuccess>
    </Wrap>
      )}
    </PageShell>
  );
}
